/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import { toast } from 'react-toastify';

const ManualIndentModal = ({
    show,
    onClose,
    prescriptionItems,
    onCreateIndent,
    existingIndentItems = [] // Items already added via "Add To Indent" buttons
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [transferFrom, setTransferFrom] = useState('easydoseStore');
    const [transferTo, setTransferTo] = useState('opPharmacy');

    // Initialize selected items from prescription items
    useEffect(() => {
        if (show && prescriptionItems) {
            const items = Object.values(prescriptionItems).map(item => {
                const prescriptionItemId = item.id;
                const isAlreadyInIndent = existingIndentItems.some(
                    indentItem => indentItem.prescriptionItemId === prescriptionItemId
                );

                return {
                    prescriptionItemId,
                    itemId: item.itemId || item.item?.id,
                    inventoryId: item.inventoryId || item.item?.inventoryId,
                    itemName: item.item?.name || item.itemName || 'Unknown Medicine',
                    selected: isAlreadyInIndent, // Pre-select if already in indent
                    quantity: item.quantity || item.doseSize || 1,
                    disabled: isAlreadyInIndent // Disable if already added via button
                };
            });
            setSelectedItems(items);
        }
    }, [show, prescriptionItems, existingIndentItems]);

    const toggleItemSelection = (index) => {
        const item = selectedItems[index];
        if (item.disabled) {
            toast.info('This item is already added via "Add To Indent" button');
            return;
        }

        setSelectedItems(prev =>
            prev.map((it, i) =>
                i === index ? { ...it, selected: !it.selected } : it
            )
        );
    };

    const updateQuantity = (index, quantity) => {
        setSelectedItems(prev =>
            prev.map((item, i) => {
                if (i === index) {
                    // Allow empty string while typing
                    if (quantity === '') {
                        return { ...item, quantity: '' };
                    }
                    const numValue = parseInt(quantity);
                    // If valid number, store it; otherwise keep as string for typing
                    if (!isNaN(numValue) && numValue >= 1) {
                        return { ...item, quantity: numValue };
                    }
                    // Keep previous value if invalid (allows backspacing/editing)
                    return item;
                }
                return item;
            })
        );
    };

    const selectableItems = selectedItems.filter(item => !item.disabled);
    const allSelectableSelected = selectableItems.length > 0 &&
        selectableItems.every(item => item.selected);

    const selectAllItems = () => {
        setSelectedItems(prev =>
            prev.map(item =>
                item.disabled
                    ? item // Keep disabled items unchanged
                    : { ...item, selected: true }
            )
        );
    };

    const deselectAllItems = () => {
        setSelectedItems(prev =>
            prev.map(item =>
                item.disabled
                    ? item // Keep disabled items unchanged
                    : { ...item, selected: false }
            )
        );
    };

    const handleCreateIndent = () => {
        const selected = selectedItems.filter(item => item.selected && !item.disabled);
        const allItems = [...existingIndentItems, ...selected];

        if (allItems.length === 0) {
            toast.warn('Please select at least one item');
            return;
        }

        const payload = {
            items: allItems.map(item => ({
                inventoryId: item.inventoryId ,
                quantity: parseInt(item.quantity)
            }))
        };

        onCreateIndent(payload);
        onClose();
    };

    const selectedCount = selectedItems.filter(item => item.selected || item.disabled).length;
    const manualSelectedCount = selectedItems.filter(item => item.selected && !item.disabled).length;

    return (
        show && (
            <Modal title="Create Manual Indent Transfer" onClose={onClose}>
                <div className="flex flex-col gap-4 p-4 max-h-[80vh] overflow-y-auto">
                    {/* Info Banner */}
                    {existingIndentItems.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                                <strong>{existingIndentItems.length} item(s)</strong> already added via &quot;Add To Indent&quot; buttons will be included.
                            </p>
                        </div>
                    )}

                    {/* Transfer From/To Selection */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Transfer From
                            </label>
                            <select
                                value={transferFrom}
                                onChange={(e) => setTransferFrom(e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            >
                                <option value="easydoseStore">Easydose Store</option>
                                <option value="opPharmacy">OP Pharmacy</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Transfer To
                            </label>
                            <select
                                value={transferTo}
                                onChange={(e) => setTransferTo(e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            >
                                <option value="easydoseStore">Easydose Store</option>
                                <option value="opPharmacy">OP Pharmacy</option>
                            </select>
                        </div>
                    </div>

                    {/* Items List */}
                    <div className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-semibold">Select Items</h3>
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600">
                                    {selectedCount} selected ({manualSelectedCount} new + {existingIndentItems.length} existing)
                                </span>
                                {selectableItems.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={allSelectableSelected ? deselectAllItems : selectAllItems}
                                        className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                    >
                                        {allSelectableSelected ? 'Deselect All' : 'Select All'}
                                    </button>
                                )}
                            </div>
                        </div>
                        {selectedItems.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">No items available</p>
                        ) : (
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {selectedItems.map((item, index) => (
                                    <div
                                        key={item.prescriptionItemId}
                                        className={`flex items-center gap-3 p-3 border rounded-lg ${item.selected || item.disabled
                                            ? item.disabled
                                                ? 'bg-green-50 border-green-300'
                                                : 'bg-blue-50 border-blue-300'
                                            : 'bg-gray-50 border-gray-200'
                                            } ${item.disabled ? 'opacity-75' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={item.selected || item.disabled}
                                            onChange={() => toggleItemSelection(index)}
                                            disabled={item.disabled}
                                            className="w-4 h-4"
                                        />
                                        <div className="flex-1">
                                            <p className="font-medium">{item.itemName}</p>
                                            <p className="text-xs text-gray-500">
                                                ID: {item.inventoryId || 'N/A'}
                                            </p>
                                            {item.disabled && (
                                                <p className="text-xs text-green-600 mt-1">
                                                    ✓ Already added via button
                                                </p>
                                            )}
                                        </div>
                                        {(item.selected || item.disabled) && (
                                            <div className="flex items-center gap-2">
                                                <label className="text-sm">Qty:</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity ?? 1} // Use nullish coalescing to handle empty string
                                                    onChange={(e) => updateQuantity(index, e.target.value)}
                                                    onBlur={(e) => {
                                                        // Ensure valid number on blur
                                                        const numValue = parseInt(e.target.value) || 1;
                                                        updateQuantity(index, String(numValue));
                                                    }}
                                                    disabled={item.disabled}
                                                    className="w-20 p-1 border rounded text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 justify-end pt-2 border-t">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateIndent}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Create Indent ({selectedCount} items)
                        </button>
                    </div>
                </div>
            </Modal>
        )
    );
};

export default ManualIndentModal;